import { useEffect, useRef, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Scanner web : BarcodeDetector natif, sinon @zxing/browser ; la caméra n'est demandée qu'au clic.
export function BarcodeScanner({ onCode }) {
  const { t } = useLang();
  const videoRef = useRef(null);
  const stopRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | running | denied | unsupported
  const [code, setCode] = useState("");

  const stop = () => { stopRef.current?.(); stopRef.current = null; setState("idle"); };
  useEffect(() => () => stopRef.current?.(), []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return setState("unsupported");
    try {
      const formats = ["ean_13", "ean_8", "upc_a", "upc_e"];
      if ("BarcodeDetector" in window) {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        videoRef.current.srcObject = stream; await videoRef.current.play();
        const detector = new window.BarcodeDetector({ formats });
        let active = true;
        const tick = async () => {
          if (!active) return;
          try { const codes = await detector.detect(videoRef.current); if (codes[0]?.rawValue) { stop(); return onCode(codes[0].rawValue); } } catch { /* frame skipped */ }
          requestAnimationFrame(tick);
        };
        stopRef.current = () => { active = false; stream.getTracks().forEach((tr) => tr.stop()); };
        setState("running"); tick();
      } else {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        const reader = new BrowserMultiFormatReader();
        const controls = await reader.decodeFromVideoDevice(undefined, videoRef.current, (result) => { if (result) { stop(); onCode(result.getText()); } });
        stopRef.current = () => controls.stop();
        setState("running");
      }
    } catch (e) { setState(e?.name === "NotAllowedError" || e?.name === "NotFoundError" ? "denied" : "unsupported"); }
  };

  const manual = () => { const c = code.replace(/\D/g, ""); if (c.length >= 8) onCode(c); };
  return (
    <div className="space-y-3" data-testid="journal-scanner">
      <p className="text-sm text-muted-foreground">{t("j_scan_hint")}</p>
      <div className="relative rounded-2xl overflow-hidden bg-foreground/90 aspect-[4/3]">
        <video ref={videoRef} className="h-full w-full object-cover" muted playsInline />
        {state === "running" && <div className="absolute inset-x-[12%] inset-y-[30%] border-2 border-primary rounded-xl pointer-events-none" />}
        {state !== "running" && <div className="absolute inset-0 grid place-items-center"><Button onClick={start} className="rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="journal-scan-start">{t("j_scan_start")}</Button></div>}
      </div>
      {state === "running" && <Button variant="outline" className="rounded-full" onClick={stop} data-testid="journal-scan-stop">{t("j_scan_stop")}</Button>}
      {state === "denied" && <p className="text-sm text-chenia-terracottadark" data-testid="journal-scan-denied">{t("j_scan_denied")}</p>}
      {state === "unsupported" && <p className="text-sm text-chenia-terracottadark" data-testid="journal-scan-unsupported">{t("j_scan_unsupported")}</p>}
      <div className="flex gap-2">
        <Input inputMode="numeric" placeholder={t("j_code_ph")} value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && manual()} className="rounded-xl" data-testid="journal-code-input" />
        <Button onClick={manual} disabled={code.replace(/\D/g, "").length < 8} className="rounded-full" data-testid="journal-code-lookup">{t("j_lookup")}</Button>
      </div>
    </div>
  );
}
